import { Controller } from "@hotwired/stimulus";
import { clusterPoints } from "../utils/clusterPoints";

// GR-3377: the live venue map on the sourcing venue step and the request
// page. The directory venues of the area being browsed arrive as clustered
// markers, the request's selected property sits in focus, and zooming breaks
// the clusters apart.
//
// Everything here is an enhancement over markup that already works. The
// server renders the GR-3130 static embed and hides the map canvas; this
// controller swaps them only once Leaflet is up and its tiles are arriving,
// and swaps them back if the tiles stop. So no JavaScript, a failed chunk, a
// blocked tile host or an org with no coordinates all land on exactly what
// the page showed before the map existed.
//
// Leaflet is imported dynamically so only these two pages pay for it, and
// behind `loadLeaflet` so the wiring can be tested without a tile server.
//
// The drill-down lane (GR-3376) owns the selects; its focus event is
// subscribed on the document, so neither partial has to know about the other.
export default class SourcingMapController extends Controller {
  static targets = ["canvas", "fallback", "status"];
  static values = { config: Object };

  // Three misses with nothing arriving is a blocked host or a dead tile
  // server, not the usual edge-of-the-world 404.
  static TILE_FAILURE_LIMIT = 3;

  // GR-3409: where the map opens before a state is chosen. Not the geographic
  // centre of the US but the centre of its venues as this box frames them: the
  // step's map is wide and short, and centring on the true centroid clipped the
  // Gulf and Florida off the bottom while spending the top on empty Canada.
  // Alaska, Hawaii and the territories are in the data and reachable by
  // panning; framing for them would push the mainland into a corner, which is
  // not the picture anyone opens this step to see.
  static US_CENTER = [38.5, -96.0];
  static US_ZOOM = 4;

  async connect() {
    this.disconnected = false;
    this.points = [];
    // GR-3411: what the venue endpoint has said about a venue, by id. A
    // marker reopened is not a venue asked about twice.
    this.venueDetails = new Map();
    this.tileFailures = 0;
    this.tilesArrived = false;
    this.region = this.configValue.region;
    this.city = this.configValue.city;
    // GR-3409: a caller that opens on the country has somewhere to draw from
    // the start. It is a baseline rather than a scope: browsing into a state
    // replaces it, and browsing back out lands here again instead of on a
    // map-shaped hole.
    this.national = Boolean(this.configValue.national);
    // GR-3392: which half of the directory the page is browsing. The drill-down
    // owns the choice; this map only ever follows it.
    this.allVenues = Boolean(this.configValue.allVenues);
    // The selection moves as the picker is driven, so it lives here rather
    // than in the Stimulus value, which is the server's opening position and
    // should stay that.
    this.focused = this.configValue.focus || null;

    await this.draw();
  }

  // Bring the map up and load whatever area is current.
  //
  // Dormant until a browse names somewhere. Step 3 renders once and the picker
  // drives its selects client-side from then on, so a map the server decided
  // not to render at all is a map no amount of browsing can bring back — which
  // is why this is always in the page and only the drawing waits. An empty
  // world map is no use either, so until there is an area it says what it is
  // waiting for.
  async draw() {
    if (!this.scoped && !this.focused) return this.standDown();

    if (!this.map) {
      try {
        await this.buildMap();
      } catch {
        // Nothing to say: the fallback is already what the page is showing.
        return;
      }
      // GR-3477: buildMap answers without a map when the page navigated away
      // during it. Nothing below may touch an element that is no longer here.
      if (!this.map) return;
    }
    this.reveal();
    await this.loadPoints();
  }

  get scoped() {
    return Boolean(this.region || this.city || this.national);
  }

  // The country is what is drawn when nothing narrower is.
  get showingNation() {
    return this.national && !this.region && !this.city;
  }

  disconnect() {
    // GR-3477: read after the awaits below, which can still be in flight.
    this.disconnected = true;
    this.cancelRecluster();
    this.map?.remove();
    this.map = null;
  }

  // Split out so a chunk that never arrives is indistinguishable from
  // Leaflet throwing on an ancient browser: both leave the fallback up.
  async loadLeaflet() {
    const [leaflet] = await Promise.all([
      import("leaflet"),
      import("leaflet/dist/leaflet.css"),
    ]);
    return leaflet.default ?? leaflet;
  }

  async buildMap() {
    const L = await this.loadLeaflet();
    // GR-3477: Leaflet arrives as a dynamic import, and a Turbo navigation
    // during it disconnects this controller. disconnect() has already run and
    // will not run again, so a map built now would hold its tile requests and
    // its map handlers on a detached element for the life of the page.
    if (this.disconnected) return;

    const config = this.configValue;

    // Leaflet measures the container when it initialises, so reveal first.
    this.reveal();

    // GR-3477: the failure budget belongs to the map it is counting for. Left
    // at the limit from the map it just took down, this one would die on its
    // first stray tile error.
    this.tileFailures = 0;

    this.map = L.map(this.canvasTarget, {
      attributionControl: true,
      minZoom: config.minZoom,
      maxZoom: config.maxZoom,
      // The map sits inside a scrolling form; a wheel over it should scroll
      // the page, not zoom the map out from under the pointer.
      scrollWheelZoom: false,
    });

    this.tiles = L.tileLayer(config.tileUrl, {
      attribution: config.attribution,
      minZoom: config.minZoom,
      maxZoom: config.maxZoom,
    });
    this.tiles.on("tileload", () => {
      this.tilesArrived = true;
    });
    this.tiles.on("tileerror", () => this.tileFailed());
    this.tiles.addTo(this.map);

    this.markers = L.layerGroup().addTo(this.map);
    this.leaflet = L;

    if (this.focused) {
      this.map.setView([this.focused.lat, this.focused.lng], config.focusZoom);
    } else if (this.showingNation) {
      this.map.setView(SourcingMapController.US_CENTER, SourcingMapController.US_ZOOM);
    } else {
      // No selection yet: open on the world and let the points frame it.
      this.map.setView([0, 0], config.minZoom);
    }

    // GR-3411: a popup is anchored to its marker, and Leaflet closes it when
    // that marker leaves the map (bindPopup binds `remove` to closePopup).
    // Re-clustering is what removes markers, so it has to know when somebody
    // is reading one.
    this.reading = false;
    this.cancelRecluster();
    this.map.on("popupopen", () => {
      this.reading = true;
    });
    this.map.on("popupclose", () => {
      this.reading = false;
      if (this.reclusterPending) this.recluster();
    });
    this.map.on("zoomend moveend", () => this.recluster());
  }

  // Zooming breaks the clusters apart — but never from inside Leaflet.
  //
  // Leaflet fires its map events from the middle of its own routines and goes
  // on using what it had afterwards. Popup._adjustPan stops a running pan
  // animation, which fires moveend synchronously, and only then re-reads
  // `this._map` to measure against; a handler that clears the marker layer in
  // between detaches the popup being opened and leaves _adjustPan holding null.
  // A click is the same story twice over: it fires preclick before click, and
  // preclick closes the open popup, so a re-cluster run from that popupclose
  // removes the marker the click pass is about to look for.
  //
  // So the layers are never touched until the stack this was called from has
  // unwound. By then Leaflet has finished opening whatever it was opening and
  // popupopen has said so, which is what makes the check below trustworthy:
  // a move that arrives during an open is a move on a map somebody is reading.
  //
  // Only a move waits. A browse somewhere else redraws through render() at
  // once, because the markers under that popup are the wrong area's now, and
  // it is called from a settled stack of our own rather than from Leaflet.
  recluster() {
    if (this.reclusterHandle) return;

    this.reclusterHandle = setTimeout(() => {
      this.reclusterHandle = null;
      if (this.reading) {
        this.reclusterPending = true;
        return;
      }
      this.render();
    });
  }

  cancelRecluster() {
    clearTimeout(this.reclusterHandle);
    this.reclusterHandle = null;
    this.reclusterPending = false;
  }

  // === the area's points ===

  async loadPoints() {
    // GR-3477: browsing outruns the network. Without this the answer for the
    // area just left can land after the answer for the area just entered, and
    // paint its markers over them.
    const token = (this.pointsToken = (this.pointsToken || 0) + 1);
    let body;
    try {
      const response = await fetch(this.pointsUrl(), {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(`points: ${response.status}`);
      body = await response.json();
    } catch {
      if (token !== this.pointsToken) return;

      this.points = [];
      if (this.map) this.say("Nearby venues could not be loaded.");
      return;
    }
    if (token !== this.pointsToken) return;

    this.points = body.points || [];
    // A blocked tile host errors while this is in flight, so tileFailed() can
    // have taken the map down and put the static embed back before the answer
    // lands. There is then nothing left to frame, and a status line about this
    // area would be describing a map the reader cannot see.
    if (!this.map) return;

    this.describe(body);
    // The national view opens on a frame we chose; fitting it to its own points
    // would let one venue in Anchorage decide how the lower 48 are drawn.
    if (!this.focused && !this.showingNation) this.frameAll();
    this.render();
  }

  pointsUrl() {
    const url = new URL(this.configValue.pointsUrl, window.location.origin);
    if (this.region) url.searchParams.set("region", this.region);
    if (this.city) url.searchParams.set("city", this.city);
    if (this.allVenues) url.searchParams.set("all_venues", "1");
    // GR-3409: explicit, because an unscoped call answers with nothing.
    if (this.showingNation) url.searchParams.set("national", "1");
    return url.toString();
  }

  // The cap is the server's (Sourcing::MapPoints::MAX_POINTS); say plainly
  // that the picture is partial rather than quietly showing half a state.
  describe({ points, total, truncated }) {
    if (truncated) {
      this.say(
        `Showing ${points.length.toLocaleString()} of ${total.toLocaleString()} venues in this area. ` +
          "Narrow to a city to see the rest."
      );
    } else {
      this.hideStatus();
    }
  }

  frameAll() {
    const coords = this.points.map((point) => [point.lat, point.lng]);
    if (coords.length === 0) return;

    this.map.fitBounds(this.leaflet.latLngBounds(coords), { padding: [24, 24] });
  }

  // === drawing ===

  render() {
    if (!this.map) return;

    // Cleared before the layers are, because clearing them closes any popup on
    // them, and that popupclose would otherwise re-enter this method halfway
    // through and draw the area twice.
    this.reclusterPending = false;
    this.markers.clearLayers();
    clusterPoints(this.points, { zoom: this.map.getZoom() }).forEach((cluster) => {
      this.markers.addLayer(
        cluster.count === 1 ? this.venueMarker(cluster.points[0]) : this.clusterMarker(cluster)
      );
    });

    if (this.focused) this.markers.addLayer(this.focusMarker(this.focused));
  }

  clusterMarker(cluster) {
    const label = `${cluster.count} venues`;
    return this.leaflet
      .marker([cluster.lat, cluster.lng], {
        title: label,
        alt: label,
        icon: this.leaflet.divIcon({
          className: "",
          html: `<div class="sourcing-map-cluster">${cluster.count}</div>`,
          iconSize: [32, 32],
        }),
      })
      .on("click", () => this.zoomInto(cluster));
  }

  venueMarker(point) {
    const popup = this.popup(point);
    return this.leaflet
      .marker([point.lat, point.lng], {
        title: point.name,
        alt: point.name,
        icon: this.pin("sourcing-map-pin", 16),
      })
      .bindPopup(popup)
      // GR-3411: on open, not on draw. A state's worth of markers would
      // otherwise be a state's worth of requests for detail nobody read.
      .on("popupopen", () => this.loadVenueDetail(point, popup));
  }

  // The request's own selection, drawn over the directory so it stays
  // findable once the area fills up.
  //
  // GR-3433: the colour is what makes it the selection; the popup is the same
  // popup, filled the same way on open, because it is the same hotel a reader
  // would be reading about under any other marker.
  focusMarker(focus) {
    const point = { ...focus, selected: true };
    const popup = this.popup(point);
    return this.leaflet
      .marker([focus.lat, focus.lng], {
        title: focus.name,
        alt: `${focus.name} (selected)`,
        zIndexOffset: 1000,
        icon: this.pin("sourcing-map-pin sourcing-map-pin--selected", 24),
      })
      .bindPopup(popup)
      .on("popupopen", () => this.loadVenueDetail(point, popup));
  }

  pin(className, size) {
    return this.leaflet.divIcon({
      className: "",
      html: `<div class="${className}"></div>`,
      iconSize: [size, size],
    });
  }

  // Built as an element rather than a string so the select control can carry
  // its own listener instead of the map delegating clicks it cannot scope.
  popup(point) {
    const node = document.createElement("div");
    const title = document.createElement("span");
    title.className = "sourcing-map-popup-title";
    title.textContent = point.name;
    node.appendChild(title);

    // GR-3433: the same lines whichever marker this is. The selection used to
    // say "Selected for this request" here and nothing else, which told a
    // reader who had just clicked the selected marker what they knew already,
    // in place of what they had opened it for.
    //
    // The address is a stand-in for a marker nothing is coming to describe:
    // the directory's own line carries the city with it, and the request's
    // copy of a promoted property's street is the same street spelled a little
    // differently, so drawing both prints it twice.
    [point.chain, this.describable(point) ? null : point.address]
      .filter(Boolean)
      .forEach((line) => {
        node.appendChild(document.createElement("br"));
        node.appendChild(document.createTextNode(line));
      });

    // GR-3411: filled from the venue's own endpoint when the popup opens,
    // and left empty when there is nothing to say.
    const details = document.createElement("div");
    details.className = "sourcing-map-popup-details";
    details.dataset.sourcingMapDetails = "";
    node.appendChild(details);

    const offer = this.selectControl(point);
    if (offer) node.appendChild(offer);
    return node;
  }

  // === what the directory knows about one venue (GR-3411) ===

  // Kept apart from the marker code above, and from the feed: Sourcing::
  // MapPoints is capped at 500 points and every byte in it is multiplied by
  // all of them, while this is one venue, asked about once, when a reader
  // opens its popup.
  async loadVenueDetail(point, node) {
    // A marker with no directory venue behind it has nothing to ask about: a
    // property added to the catalog by hand was never in the directory. A page
    // that configured no endpoint at all (a map mounted without one) has
    // nothing to add either. Both leave the popup as it was served, which is
    // what a directory venue carrying no tags shows too.
    if (!this.describable(point)) return;

    const box = node.querySelector("[data-sourcing-map-details]");
    if (!box || box.dataset.filled) return;

    const detail = await this.fetchVenueDetail(this.venueIdFor(point));
    if (!detail) return;

    box.dataset.filled = "1";
    box.appendChild(this.venueDetailNode(detail, point));
  }

  // A directory marker is the venue it draws. The focused marker is the
  // request's own selection, whose id names a catalog property (GR-3116), so
  // the directory venue it was promoted from travels beside it.
  venueIdFor(point) {
    return point.selected ? point.venue_id : point.id;
  }

  // Whether the directory is going to say anything about this marker: a venue
  // to ask about, and somewhere to ask. Asked before the popup is built as
  // well as when it opens, because what the popup draws for itself depends on
  // what is coming to join it.
  describable(point) {
    return Boolean(this.venueIdFor(point) && this.configValue.venueDetailUrl);
  }

  // The promise is cached rather than its result, so a second open while the
  // first is in flight joins it instead of asking again. A failure is not
  // cached: a blocked request now should not blank this popup for the rest of
  // the session.
  fetchVenueDetail(id) {
    if (this.venueDetails.has(id)) return this.venueDetails.get(id);

    const pending = fetch(this.venueDetailUrl(id), { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    this.venueDetails.set(id, pending);
    pending.then((detail) => {
      if (!detail) this.venueDetails.delete(id);
    });
    return pending;
  }

  venueDetailUrl(id) {
    const url = new URL(this.configValue.venueDetailUrl, window.location.origin);
    url.searchParams.set("venue_id", String(id));
    return url.toString();
  }

  // Most venues carry some of these and few carry all, so every line is drawn
  // only if the venue has it: an absent tag leaves no empty row behind.
  venueDetailNode(detail, point) {
    const fragment = document.createDocumentFragment();

    // The chain is already on the marker's own line. A brand repeating it is
    // noise; a brand saying something else is worth its own chip.
    const chain = String(point.chain || "").toLowerCase();
    if (detail.brand && detail.brand.toLowerCase() !== chain) {
      fragment.appendChild(this.venueDetailChip(detail.brand));
    }

    [
      [detail.address, detail.city].filter(Boolean).join(", "),
      detail.rooms ? `${detail.rooms.toLocaleString()} rooms` : null,
      detail.phone,
    ]
      .filter(Boolean)
      .forEach((line) => fragment.appendChild(this.venueDetailLine(line)));

    if (this.linkable(detail.website)) fragment.appendChild(this.venueDetailLink(detail.website));
    return fragment;
  }

  // DirectoryVenue#website already refuses anything that is not http(s), but
  // the value starts life as an OpenStreetMap tag anyone can edit and ends up
  // as an href, so the scheme is guaranteed here, where the href is written,
  // rather than trusted from the other end of a JSON endpoint.
  linkable(website) {
    if (!website) return false;

    try {
      return ["http:", "https:"].includes(new URL(website).protocol);
    } catch {
      return false;
    }
  }

  venueDetailChip(brand) {
    const chip = document.createElement("span");
    chip.className = "Label sourcing-map-popup-chip";
    chip.textContent = brand;
    return chip;
  }

  venueDetailLine(text) {
    const line = document.createElement("div");
    line.textContent = text;
    return line;
  }

  // Somebody else's site, from open data: a new tab, and no window.opener or
  // referrer handed to it.
  venueDetailLink(website) {
    const link = document.createElement("a");
    link.href = website;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    // The host is what a reader recognises; the whole URL is rarely readable
    // in a popup this size.
    link.textContent = this.websiteLabel(website);
    return link;
  }

  websiteLabel(website) {
    try {
      return new URL(website).hostname.replace(/^www\./, "");
    } catch {
      return website;
    }
  }

  // === adding a marker to the request's preferences ===

  // The map browses further than the picker's list can reach, so a marker is
  // often the only place a venue is on offer. Rather than inventing a way to
  // pick it, this drives GR-3376's own select: its values already name a
  // catalog property outright and a directory venue as directory:<id>, and the
  // step promotes on save. Firing its events lets the rest of its wiring —
  // the caption, the focus dispatch back to this map — run as if the reader
  // had used the dropdown.
  //
  // GR-3391: and then it submits the step's own Add to Preferences button.
  // Setting the select and stopping there left the reader one further,
  // undiscoverable click from the shortlist they had just asked to be on, and
  // the label said as much. Driving the step's button rather than posting from
  // here means a marker add is the same request, the same frame and the same
  // server path as a button add; nothing in this file knows how a preference
  // is made.
  selectControl(point) {
    if (point.selected || !this.propertySelect) return null;

    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn btn-sm mt-2 d-block";
    button.dataset.sourcingMapSelect = String(point.property_id ?? `directory:${point.id}`);
    button.textContent = "Add to preferences";
    button.addEventListener("click", () => this.addPoint(point));
    return button;
  }

  addPoint(point) {
    this.selectPoint(point);
    this.submitPreference();
  }

  // The quote form reuses the same picker with no add button beside it, so a
  // missing button is a page where selecting is all there is to do, not a
  // fault.
  get addPreferenceButton() {
    return document.querySelector('[name="add_preference"]');
  }

  submitPreference() {
    const button = this.addPreferenceButton;
    const form = button?.form;
    if (!form) return;

    // The submitter is what puts add_preference in the params, which is what
    // tells the step this save is an add rather than a Save & Continue.
    if (form.requestSubmit) form.requestSubmit(button);
    else button.click();
  }

  get propertySelect() {
    return (
      document.querySelector('[data-sourcing-property-picker-target="property"]') ||
      document.querySelector('select[name="sourcing_property_id"]')
    );
  }

  selectPoint(point) {
    const select = this.propertySelect;
    if (!select) return;

    const value = String(point.property_id ?? `directory:${point.id}`);
    // The picker's list is capped (GR-3376's DIRECTORY_LIMIT) and the map's is
    // not, so a marker regularly names a venue the select never offered. The
    // server resolves the value either way, so the option is added rather than
    // the click quietly doing nothing. Compared over the options rather than
    // matched by selector: "directory:5" needs escaping to be one, and there
    // is nothing here a plain comparison cannot answer.
    const offered = Array.from(select.options).some((option) => option.value === value);
    if (!offered) select.appendChild(this.optionFor(point, value));
    select.value = value;

    // input then change, the order a real selection fires them in.
    select.dispatchEvent(new Event("input", { bubbles: true }));
    select.dispatchEvent(new Event("change", { bubbles: true }));
    this.map?.closePopup();
  }

  // Carries the coordinates, because the picker reads them straight back off
  // the selected option to work out where to send this map.
  optionFor(point, value) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = point.name;
    option.dataset.lat = String(point.lat);
    option.dataset.lng = String(point.lng);
    if (this.region) option.dataset.region = this.region;
    return option;
  }

  zoomInto(cluster) {
    const zoom = Math.min(this.map.getZoom() + 2, this.configValue.maxZoom);
    this.map.setView([cluster.lat, cluster.lng], zoom);
  }

  // === the drill-down lane's focus event ===

  // Dispatched on the document by the property picker (GR-3376) on every
  // browse: the area always, the selected option's coordinates once there is a
  // selection. An area with no point is a browse, not a non-event — it is the
  // whole reason this map exists, so it re-scopes and frames the new area.
  async focus(event) {
    const detail = event?.detail || {};
    const lat = Number(detail.latitude ?? detail.lat);
    const lng = Number(detail.longitude ?? detail.lng);
    const placed = Number.isFinite(lat) && Number.isFinite(lng);

    const moved = this.rescope(detail);
    // No point means nothing is selected, so nothing is in focus. Leaving the
    // last marker up would strand it over whatever area is drawn next.
    this.focused = placed
      ? { name: detail.name || "Selected property", lat, lng, venue_id: detail.venueId || null }
      : null;

    // Dormant, or browsed back to nowhere: draw() settles both.
    if (!this.map || (!this.scoped && !this.focused)) return this.draw();

    if (placed) this.map.setView([lat, lng], this.configValue.focusZoom);
    // GR-3409 with GR-3410: browsing back out past the state, or clearing the
    // property from a national browse, lands on the country again rather than
    // leaving the map zoomed into wherever it last was.
    else if (moved && this.showingNation) {
      this.map.setView(SourcingMapController.US_CENTER, SourcingMapController.US_ZOOM);
    }
    // A move reloads through draw() rather than loadPoints() directly, so a
    // map coming back from dormant is revealed on the way.
    if (moved) return this.draw();

    this.render();
  }

  // Browsing into another state or city means another set of markers.
  rescope({ region, city, allVenues }) {
    if (region === undefined && city === undefined && allVenues === undefined) return false;

    // A focus event that says nothing about the scope is not a request to
    // change it: other lanes dispatch these too.
    const next = {
      region: region || null,
      city: city || null,
      allVenues: allVenues === undefined ? this.allVenues : Boolean(allVenues),
    };
    if (
      next.region === (this.region || null) &&
      next.city === (this.city || null) &&
      next.allVenues === this.allVenues
    ) {
      return false;
    }

    this.region = next.region;
    this.city = next.city;
    this.allVenues = next.allVenues;
    return true;
  }

  // === the fallback ===

  reveal() {
    this.fallbackTarget.hidden = true;
    if (!this.canvasTarget.hidden) return;

    this.canvasTarget.hidden = false;
    // Coming back from dormant: Leaflet measured a hidden box, so it has to
    // be told the box has a size now.
    this.map?.invalidateSize?.();
  }

  // Nowhere to draw: keep the map built but out of sight, and say what would
  // bring it back. Cheaper than tearing Leaflet down, and picking a state
  // again is then instant.
  standDown() {
    this.canvasTarget.hidden = true;
    this.points = [];
    if (this.configValue.prompt) this.say(this.configValue.prompt);
    else this.hideStatus();
  }

  tileFailed() {
    if (this.tilesArrived) return;

    this.tileFailures += 1;
    if (this.tileFailures < SourcingMapController.TILE_FAILURE_LIMIT) return;

    // The tile host is unreachable or blocked. Put back what the page had.
    this.cancelRecluster();
    this.map?.remove();
    this.map = null;
    this.canvasTarget.hidden = true;
    this.fallbackTarget.hidden = false;
    this.hideStatus();
  }

  say(message) {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = message;
    this.statusTarget.hidden = false;
  }

  hideStatus() {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = "";
    this.statusTarget.hidden = true;
  }
}
